"use client";

import { useEffect, useRef, useState } from "react";
import { decode } from "blurhash";
import { cn } from "@/lib/cn";
import { buildSrcSet } from "@/lib/media";
import type { MediaUrls } from "@/lib/types";

export interface BlurImageProps {
  urls: MediaUrls;
  blurhash: string;
  width: number;
  height: number;
  alt: string;
  /** Responsive `sizes` attribute (required for correct srcset selection). */
  sizes: string;
  className?: string;
  /** Eager-load (above the fold). Defaults to lazy. */
  priority?: boolean;
  /**
   * Fill the positioned parent instead of establishing the image's intrinsic
   * aspect ratio. The parent must set `position` and a size (e.g. an
   * `aspect-[3/4]` box) — the image cover-crops into it.
   */
  fill?: boolean;
}

const HASH_SIZE = 32;

/**
 * Responsive image with a decoded blurhash placeholder. The placeholder paints
 * instantly on a canvas; the real image (served via srcset/sizes) fades in on
 * load. Fills its container — set aspect ratio / size on the wrapper.
 */
export function BlurImage({
  urls,
  blurhash,
  width,
  height,
  alt,
  sizes,
  className,
  priority = false,
  fill = false,
}: BlurImageProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!blurhash) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      const pixels = decode(blurhash, HASH_SIZE, HASH_SIZE);
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const imageData = ctx.createImageData(HASH_SIZE, HASH_SIZE);
      imageData.data.set(pixels);
      ctx.putImageData(imageData, 0, 0);
    } catch {
      // Invalid hash — leave the canvas blank; the image still loads.
    }
  }, [blurhash]);

  return (
    <div
      className={cn(
        "overflow-hidden bg-white/5",
        fill ? "absolute inset-0 size-full" : "relative",
        className,
      )}
      style={fill ? undefined : { aspectRatio: `${width} / ${height}` }}
    >
      <canvas
        ref={canvasRef}
        width={HASH_SIZE}
        height={HASH_SIZE}
        aria-hidden="true"
        className={cn(
          "absolute inset-0 size-full scale-110 blur-[2px] transition-opacity duration-700",
          loaded ? "opacity-0" : "opacity-100",
        )}
      />
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={urls.md}
        srcSet={buildSrcSet(urls)}
        sizes={sizes}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        onLoad={() => setLoaded(true)}
        className={cn(
          "absolute inset-0 size-full object-cover transition-opacity duration-700",
          loaded ? "opacity-100" : "opacity-0",
        )}
      />
    </div>
  );
}
